import React from 'react';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';

var API = 'https://api.algrok.com';

// ── Black-Scholes IV calculation (same as OptionChain) ──
function normCDF(x) {
  var a1 = 0.254829592, a2 = -0.284496736, a3 = 1.421413741;
  var a4 = -1.453152027, a5 = 1.061405429, p = 0.3275911;
  var sign = x < 0 ? -1 : 1;
  x = Math.abs(x) / Math.SQRT2;
  var t = 1.0 / (1.0 + p * x);
  var y = 1.0 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);
  return 0.5 * (1.0 + sign * y);
}
function bsPrice(S, K, T, r, sigma, isCall) {
  if (T <= 0 || sigma <= 0) return 0;
  var d1 = (Math.log(S / K) + (r + sigma * sigma / 2) * T) / (sigma * Math.sqrt(T));
  var d2 = d1 - sigma * Math.sqrt(T);
  if (isCall) return S * normCDF(d1) - K * Math.exp(-r * T) * normCDF(d2);
  return K * Math.exp(-r * T) * normCDF(-d2) - S * normCDF(-d1);
}
function calcIV(S, K, T, r, marketPrice, isCall) {
  if (!S || !K || !T || T <= 0 || !marketPrice || marketPrice <= 0) return null;
  var sigma = 0.3;
  for (var i = 0; i < 50; i++) {
    var price = bsPrice(S, K, T, r, sigma, isCall);
    var d1 = (Math.log(S / K) + (r + sigma * sigma / 2) * T) / (sigma * Math.sqrt(T));
    var vega = S * Math.sqrt(T) * Math.exp(-d1 * d1 / 2) / Math.sqrt(2 * Math.PI);
    if (vega < 1e-10) break;
    var diff = price - marketPrice;
    sigma = sigma - diff / vega;
    if (sigma <= 0.001) sigma = 0.001;
    if (sigma > 5) sigma = 5;
    if (Math.abs(diff) < 0.01) break;
  }
  return sigma > 0.001 && sigma < 5 ? sigma * 100 : null;
}
function getTimeToExpiry(expiryStr) {
  var exp = new Date(expiryStr);
  exp.setHours(15, 30, 0, 0);
  var now = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  var diffMs = exp.getTime() - now.getTime();
  if (diffMs <= 0) return 0.0001;
  return diffMs / (365.25 * 24 * 60 * 60 * 1000);
}

// Extract IV — first try reading from data, then calculate from LTP
function getIV(item, side, spotPrice, expiryStr) {
  if (!item) return null;
  var strike = getStrike(item);
  // Try pre-calculated IV fields first
  var flatKey = side.toLowerCase() + '_iv';
  if (item[flatKey] != null && item[flatKey] > 0) return item[flatKey];
  var upKey = side.toUpperCase() + '_IV';
  if (item[upKey] != null && item[upKey] > 0) return item[upKey];
  var nested = item[side.toUpperCase()] || item[side.toLowerCase()] || {};
  if (nested.impliedVolatility && nested.impliedVolatility > 0) return nested.impliedVolatility;
  if (nested.iv && nested.iv > 0) return nested.iv;

  // Calculate from LTP using Black-Scholes
  if (!spotPrice || !expiryStr || !strike) return null;
  var ltp = getLtp(item, side);
  if (!ltp || ltp <= 0) return null;
  var T = getTimeToExpiry(expiryStr);
  var isCall = side.toUpperCase() === 'CE';
  return calcIV(spotPrice, strike, T, 0.07, ltp, isCall);
}
function getLtp(item, side) {
  if (!item) return 0;
  var flatKey = side.toLowerCase() + '_ltp';
  if (item[flatKey] != null) return item[flatKey];
  var nested = item[side.toUpperCase()] || item[side.toLowerCase()] || {};
  return nested.lastPrice || nested.last_price || nested.ltp || nested.LTP || 0;
}
function getOi(item, side) {
  if (!item) return 0;
  var flatKey = side.toLowerCase() + '_oi';
  if (item[flatKey] != null) return item[flatKey];
  var nested = item[side.toUpperCase()] || item[side.toLowerCase()] || {};
  return nested.openInterest || nested.oi || nested.OI || nested.open_interest || 0;
}
function getStrike(item) {
  return item.strike || item.strikePrice || item.strike_price || 0;
}

export default function IVSurface() {
  var [expiries, setExpiries] = useState([]);
  var [chains, setChains] = useState({});
  var [spot, setSpot] = useState(0);
  var [atm, setAtm] = useState(0);
  var [loading, setLoading] = useState(true);
  var [updateInterval, setUpdateInterval] = useState(5);
  var [lastUpdate, setLastUpdate] = useState('');
  var [surfaceMode, setSurfaceMode] = useState('both'); // ce, pe, both
  var [strikeRange, setStrikeRange] = useState(20);
  var [smileExpiry, setSmileExpiry] = useState('');
  var [smileOverlay, setSmileOverlay] = useState(false);
  var plotRef = useRef(null);
  var plotInited = useRef(false);
  var defaultCamera = useRef({ eye: { x: 1.8, y: -1.6, z: 0.9 } });
  var [plotlyReady, setPlotlyReady] = useState(!!window.Plotly);

  // Wait for Plotly CDN to load
  useEffect(function () {
    if (window.Plotly) { setPlotlyReady(true); return; }
    var check = setInterval(function () {
      if (window.Plotly) { setPlotlyReady(true); clearInterval(check); }
    }, 200);
    return function () { clearInterval(check); };
  }, []);

  // Fetch expiries
  var fetchExpiries = useCallback(function () {
    fetch(API + '/expiry?index=NIFTY&exchange=NFO', { signal: AbortSignal.timeout(5000) })
      .then(function (r) { return r.json(); })
      .then(function (d) {
        var exps = (d.all_expiries || d.expiries || []).slice(0, 5);
        setExpiries(exps);
        if (!smileExpiry && exps.length) setSmileExpiry(exps[0]);
      }).catch(function () { });
  }, [smileExpiry]);

  // Fetch chain for one expiry
  var fetchChain = useCallback(function (expiry) {
    return fetch(API + '/optionchain?index=NIFTY&expiry=' + expiry + '&exchange=NFO', { signal: AbortSignal.timeout(8000) })
      .then(function (r) { return r.json(); })
      .then(function (d) {
        return { expiry: expiry, data: d };
      }).catch(function () { return { expiry: expiry, data: null }; });
  }, []);

  // Fetch all chains
  var fetchAll = useCallback(function () {
    if (!expiries.length) return;
    Promise.all(expiries.map(fetchChain)).then(function (results) {
      var newChains = {};
      results.forEach(function (r) {
        if (r.data) {
          var chain = r.data.chain || r.data.data || r.data.options || [];
          if (chain.length) newChains[r.expiry] = { chain: chain, spot: r.data.spot };
        }
      });
      if (Object.keys(newChains).length) {
        setChains(newChains);
        // Get spot from first chain
        var firstKey = expiries.find(function(e) { return newChains[e]; });
        var first = firstKey ? newChains[firstKey] : null;
        if (first && first.spot) {
          setSpot(first.spot);
          setAtm(Math.round(first.spot / 50) * 50);
        }
      }
      setLoading(false);
      setLastUpdate(new Date().toLocaleTimeString('en-IN', { hour12: false }));
    });
  }, [expiries, fetchChain]);

  useEffect(function () { fetchExpiries(); }, [fetchExpiries]);
  useEffect(function () {
    if (!expiries.length) return;
    fetchAll();
    var id = setInterval(fetchAll, updateInterval * 1000);
    return function () { clearInterval(id); };
  }, [expiries, updateInterval, fetchAll]);

  // Build surface data
  var surfaceData = useMemo(function () {
    if (!Object.keys(chains).length || !atm) return null;
    var step = 50;
    var minStrike = atm - strikeRange * step;
    var maxStrike = atm + strikeRange * step;
    var strikes = [];
    for (var s = minStrike; s <= maxStrike; s += step) strikes.push(s);

    var expiryLabels = expiries.filter(function (e) { return chains[e]; });
    if (!expiryLabels.length) return null;

    // Build z matrices
    var z_ce = [], z_pe = [], z_both = [];
    expiryLabels.forEach(function (exp) {
      var chain = chains[exp].chain || [];
      var expSpot = chains[exp].spot || spot;
      var ceRow = [], peRow = [], bothRow = [];
      strikes.forEach(function (strike) {
        var item = chain.find(function (c) { return getStrike(c) === strike; });
        var ceIv = getIV(item, 'CE', expSpot, exp);
        var peIv = getIV(item, 'PE', expSpot, exp);
        ceRow.push(ceIv || 0);
        peRow.push(peIv || 0);
        bothRow.push(ceIv && peIv ? (ceIv + peIv) / 2 : ceIv || peIv || 0);
      });
      z_ce.push(ceRow);
      z_pe.push(peRow);
      z_both.push(bothRow);
    });

    return {
      strikes: strikes,
      expiries: expiryLabels.map(function (e) {
        var d = new Date(e);
        return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
      }),
      expiryRaw: expiryLabels,
      z_ce: z_ce, z_pe: z_pe, z_both: z_both
    };
  }, [chains, atm, strikeRange, expiries, spot]);

  // Interpretation — adapts to surfaceMode, strikeRange
  var interp = useMemo(function () {
    if (!surfaceData || !Object.keys(chains).length) return null;
    var nearExp = expiries[0];
    var chain = chains[nearExp] && chains[nearExp].chain ? chains[nearExp].chain : [];
    if (!chain.length) return null;

    var atmItem = chain.find(function (c) { return getStrike(c) === atm; });
    var atmCeIv = getIV(atmItem, 'CE', spot, nearExp) || 0;
    var atmPeIv = getIV(atmItem, 'PE', spot, nearExp) || 0;
    var atmIv = surfaceMode === 'ce' ? atmCeIv : surfaceMode === 'pe' ? atmPeIv : (atmCeIv + atmPeIv) / 2;

    // Wing IVs — scale wing distance to strike range
    var wingNear = Math.min(200, strikeRange * 50 * 0.4);
    var wingFar = Math.min(400, strikeRange * 50 * 0.8);
    var peWing = [], ceWing = [];
    chain.forEach(function (c) {
      var dist = getStrike(c) - atm;
      var ceIv = getIV(c, 'CE', spot, nearExp);
      var peIv = getIV(c, 'PE', spot, nearExp);
      if (dist >= wingNear && dist <= wingFar && ceIv) ceWing.push(ceIv);
      if (dist <= -wingNear && dist >= -wingFar && peIv) peWing.push(peIv);
    });
    var avgPeWing = peWing.length ? peWing.reduce(function (a, b) { return a + b; }, 0) / peWing.length : 0;
    var avgCeWing = ceWing.length ? ceWing.reduce(function (a, b) { return a + b; }, 0) / ceWing.length : 0;
    var skewRatio = avgCeWing > 0 ? avgPeWing / avgCeWing : 0;

    // Skew grade with reverse skew detection
    var skewGrade = 'Flat';
    if (skewRatio > 1.8) skewGrade = 'Steep PE';
    else if (skewRatio > 1.3) skewGrade = 'PE Heavy';
    else if (skewRatio >= 0.85) skewGrade = 'Flat';
    else if (skewRatio >= 0.6) skewGrade = 'CE Heavy';
    else if (skewRatio > 0) skewGrade = 'Steep CE';

    // Term structure
    var farExp = expiries.length > 1 ? expiries[expiries.length - 1] : null;
    var farChain = farExp && chains[farExp] ? chains[farExp].chain || [] : [];
    var farAtm = farChain.find(function (c) { return getStrike(c) === atm; });
    var farIv = farAtm ? ((getIV(farAtm, 'CE', spot, farExp) || 0) + (getIV(farAtm, 'PE', spot, farExp) || 0)) / 2 : 0;
    var termStructure = farIv > atmIv ? 'contango' : farIv < atmIv ? 'backwardation' : 'flat';

    // Surface roughness
    var allIvs = [];
    chain.forEach(function (c) {
      var ceIv = getIV(c, 'CE', spot, nearExp);
      var peIv = getIV(c, 'PE', spot, nearExp);
      if (surfaceMode !== 'pe' && ceIv) allIvs.push(ceIv);
      if (surfaceMode !== 'ce' && peIv) allIvs.push(peIv);
    });
    var mean = allIvs.length ? allIvs.reduce(function (a, b) { return a + b; }, 0) / allIvs.length : 0;
    var variance = allIvs.length ? allIvs.reduce(function (a, b) { return a + (b - mean) * (b - mean); }, 0) / allIvs.length : 0;
    var roughness = Math.sqrt(variance);

    // ── DIRECTIONAL SIGNAL ──
    // Score: positive = bullish, negative = bearish
    var dirScore = 0;
    var dirReasons = [];

    // 1. Skew direction (PE expensive = bearish fear, CE expensive = bullish demand)
    if (skewRatio > 1.5) { dirScore -= 2; dirReasons.push('PE skew steep (-2)'); }
    else if (skewRatio > 1.2) { dirScore -= 1; dirReasons.push('PE skew moderate (-1)'); }
    else if (skewRatio < 0.7) { dirScore += 2; dirReasons.push('CE skew steep (+2)'); }
    else if (skewRatio < 0.85) { dirScore += 1; dirReasons.push('CE skew moderate (+1)'); }

    // 2. ATM PE vs CE IV — higher PE IV = more downside fear
    var atmSkew = atmPeIv > 0 && atmCeIv > 0 ? (atmPeIv - atmCeIv) / atmCeIv * 100 : 0;
    if (atmSkew > 20) { dirScore -= 1; dirReasons.push('ATM PE IV +' + atmSkew.toFixed(0) + '% vs CE (-1)'); }
    else if (atmSkew < -20) { dirScore += 1; dirReasons.push('ATM CE IV +' + Math.abs(atmSkew).toFixed(0) + '% vs PE (+1)'); }

    // 3. Term structure — backwardation = near-term stress
    if (termStructure === 'backwardation' && atmIv - farIv > 5) { dirScore -= 1; dirReasons.push('Strong backwardation (-1)'); }
    else if (termStructure === 'contango' && farIv - atmIv > 3) { dirScore += 1; dirReasons.push('Contango — calm near-term (+1)'); }

    var dirLabel = dirScore >= 2 ? 'BULLISH' : dirScore >= 1 ? 'Mild Bull' : dirScore <= -2 ? 'BEARISH' : dirScore <= -1 ? 'Mild Bear' : 'Neutral';
    var dirColor = dirScore >= 2 ? '#00d4aa' : dirScore >= 1 ? '#4ade80' : dirScore <= -2 ? '#ff4466' : dirScore <= -1 ? '#fbbf24' : '#8899aa';

    // ── SIGNALS ──
    var signals = [];

    // Skew signals
    if (skewRatio > 1.8) signals.push({ type: 'danger', msg: 'PE skew steep (' + skewRatio.toFixed(2) + 'x) — crash fear elevated. Caution with naked PE sells.' });
    else if (skewRatio > 1.3) signals.push({ type: 'warning', msg: 'Moderate PE skew (' + skewRatio.toFixed(2) + 'x) — normal fear premium. PE credit spreads collect more.' });
    else if (skewRatio >= 0.85) signals.push({ type: 'good', msg: 'Balanced skew (' + skewRatio.toFixed(2) + 'x) — symmetric strategies favorable (straddle/iron fly).' });
    else if (skewRatio >= 0.6) signals.push({ type: 'info', msg: 'Reverse skew (' + skewRatio.toFixed(2) + 'x) — CE demand elevated. Bullish flow or hedging activity.' });
    else if (skewRatio > 0) signals.push({ type: 'warning', msg: 'Strong reverse skew (' + skewRatio.toFixed(2) + 'x) — unusual CE demand. Check for event/news.' });

    // Term structure
    if (termStructure === 'contango') signals.push({ type: 'info', msg: 'Contango — near IV (' + atmIv.toFixed(1) + ') < far IV (' + farIv.toFixed(1) + '). Calendar spreads favorable.' });
    else if (termStructure === 'backwardation') signals.push({ type: 'warning', msg: 'Backwardation — near IV (' + atmIv.toFixed(1) + ') > far IV (' + farIv.toFixed(1) + '). Near-term stress or expiry effect.' });

    // Vol regime
    if (atmIv < 12) signals.push({ type: 'good', msg: 'Very low vol (' + atmIv.toFixed(1) + '%) — premium selling paradise. High theta/gamma ratio.' });
    else if (atmIv < 16) signals.push({ type: 'good', msg: 'Low vol (' + atmIv.toFixed(1) + '%) — straddle/iron fly theta-rich.' });
    else if (atmIv > 25) signals.push({ type: 'danger', msg: 'Very high vol (' + atmIv.toFixed(1) + '%) — wide stops, reduce size, consider buying vol.' });
    else if (atmIv > 20) signals.push({ type: 'warning', msg: 'Elevated vol (' + atmIv.toFixed(1) + '%) — wider wings needed, expect fast moves.' });

    // Surface quality
    if (roughness > 8) signals.push({ type: 'danger', msg: 'Very rough surface (σ=' + roughness.toFixed(1) + ') — extreme stress, IV discontinuities, avoid complex spreads.' });
    else if (roughness > 5) signals.push({ type: 'warning', msg: 'Rough surface (σ=' + roughness.toFixed(1) + ') — market stress, pricing gaps possible.' });
    else signals.push({ type: 'good', msg: 'Smooth surface (σ=' + roughness.toFixed(1) + ') — efficient pricing, clean execution.' });

    // Directional signal
    signals.push({ type: dirScore >= 1 ? 'good' : dirScore <= -1 ? 'danger' : 'info',
      msg: 'Direction: ' + dirLabel + ' (score ' + (dirScore > 0 ? '+' : '') + dirScore + '). ' + dirReasons.join(', ') + '.' });

    // ── STRATEGY RECOMMENDATIONS ──
    var recs = [];
    var step = 50;
    var nearExpLabel = expiries[0] || '';
    var farExpLabel = expiries.length > 1 ? expiries[1] : '';

    // Build IV map for quick lookup: {strike: {ceIv, peIv, ceLtp, peLtp}}
    var ivMap = {};
    chain.forEach(function (c) {
      var s = getStrike(c);
      ivMap[s] = {
        ceIv: getIV(c, 'CE', spot, nearExp) || 0,
        peIv: getIV(c, 'PE', spot, nearExp) || 0,
        ceLtp: getLtp(c, 'CE'),
        peLtp: getLtp(c, 'PE')
      };
    });

    // Far expiry IV map (for calendar spread)
    var farIvMap = {};
    farChain.forEach(function (c) {
      var s = getStrike(c);
      farIvMap[s] = {
        ceIv: getIV(c, 'CE', spot, farExp) || 0,
        peIv: getIV(c, 'PE', spot, farExp) || 0,
        ceLtp: getLtp(c, 'CE'),
        peLtp: getLtp(c, 'PE')
      };
    });

    // 1. CALENDAR SPREAD — when term structure shows opportunity
    if (termStructure === 'backwardation' && farExpLabel) {
      // Near IV > Far IV → sell near (expensive), buy far (cheap)
      var nearD = ivMap[atm] || {};
      var farD = farIvMap[atm] || {};
      var nearExpShort = new Date(nearExpLabel).toLocaleDateString('en-IN', {day:'2-digit',month:'short'});
      var farExpShort = new Date(farExpLabel).toLocaleDateString('en-IN', {day:'2-digit',month:'short'});
      if (nearD.ceIv > 0 && farD.ceIv > 0 && nearD.ceIv - farD.ceIv > 3) {
        var side = dirScore >= 1 ? 'PE' : dirScore <= -1 ? 'CE' : (nearD.ceIv > nearD.peIv ? 'CE' : 'PE');
        recs.push({ type: 'calendar', label: 'Calendar Spread',
          detail: 'Sell ' + atm + side + ' ' + nearExpShort + ' / Buy ' + atm + side + ' ' + farExpShort,
          reason: 'Near IV ' + (side==='CE'?nearD.ceIv:nearD.peIv).toFixed(1) + '% > Far IV ' + (side==='CE'?farD.ceIv:farD.peIv).toFixed(1) + '%. Sell expensive near, buy cheap far.',
          color: '#a78bfa' });
      }
    } else if (termStructure === 'contango' && farExpLabel) {
      var nearD2 = ivMap[atm] || {};
      var farD2 = farIvMap[atm] || {};
      var nearExpShort2 = new Date(nearExpLabel).toLocaleDateString('en-IN', {day:'2-digit',month:'short'});
      var farExpShort2 = new Date(farExpLabel).toLocaleDateString('en-IN', {day:'2-digit',month:'short'});
      if (farD2.ceIv > 0 && nearD2.ceIv > 0 && farD2.ceIv - nearD2.ceIv > 3) {
        var side2 = dirScore >= 1 ? 'CE' : dirScore <= -1 ? 'PE' : 'PE';
        recs.push({ type: 'calendar', label: 'Calendar Spread',
          detail: 'Buy ' + atm + side2 + ' ' + nearExpShort2 + ' / Sell ' + atm + side2 + ' ' + farExpShort2,
          reason: 'Far IV ' + (side2==='CE'?farD2.ceIv:farD2.peIv).toFixed(1) + '% > Near IV ' + (side2==='CE'?nearD2.ceIv:nearD2.peIv).toFixed(1) + '%. Sell expensive far, buy cheap near.',
          color: '#a78bfa' });
      }
    }

    // 2. RATIO SPREAD — when wing IV is significantly higher than ATM
    // Find OTM strikes with highest IV (rich premium to sell)
    var bestRatioCe = null, bestRatioPe = null;
    for (var rs = atm + step; rs <= atm + step * 6; rs += step) {
      var d = ivMap[rs];
      if (d && d.ceIv > atmCeIv * 1.3 && d.ceLtp > 5) {
        if (!bestRatioCe || d.ceIv > bestRatioCe.iv) bestRatioCe = { strike: rs, iv: d.ceIv, ltp: d.ceLtp };
      }
    }
    for (var rs2 = atm - step; rs2 >= atm - step * 6; rs2 -= step) {
      var d2 = ivMap[rs2];
      if (d2 && d2.peIv > atmPeIv * 1.3 && d2.peLtp > 5) {
        if (!bestRatioPe || d2.peIv > bestRatioPe.iv) bestRatioPe = { strike: rs2, iv: d2.peIv, ltp: d2.peLtp };
      }
    }
    if (bestRatioPe && dirScore >= 0) {
      recs.push({ type: 'ratio', label: 'Ratio Spread (PE)',
        detail: 'Buy 1x ' + atm + 'PE / Sell 2x ' + bestRatioPe.strike + 'PE',
        reason: 'PE wing IV ' + bestRatioPe.iv.toFixed(1) + '% vs ATM ' + atmPeIv.toFixed(1) + '% (' + (bestRatioPe.iv/atmPeIv*100-100).toFixed(0) + '% rich). Collect wing premium.',
        color: '#f97316' });
    }
    if (bestRatioCe && dirScore <= 0) {
      recs.push({ type: 'ratio', label: 'Ratio Spread (CE)',
        detail: 'Buy 1x ' + atm + 'CE / Sell 2x ' + bestRatioCe.strike + 'CE',
        reason: 'CE wing IV ' + bestRatioCe.iv.toFixed(1) + '% vs ATM ' + atmCeIv.toFixed(1) + '% (' + (bestRatioCe.iv/atmCeIv*100-100).toFixed(0) + '% rich). Collect wing premium.',
        color: '#f97316' });
    }

    // 3. HEDGE SELECTION — find cheapest IV OTM for hedges
    var bestCeHedge = null, bestPeHedge = null;
    for (var hs = atm + step * 2; hs <= atm + step * 8; hs += step) {
      var hd = ivMap[hs];
      if (hd && hd.ceIv > 0 && hd.ceLtp >= 2) {
        if (!bestCeHedge || hd.ceIv < bestCeHedge.iv) bestCeHedge = { strike: hs, iv: hd.ceIv, ltp: hd.ceLtp };
      }
    }
    for (var hs2 = atm - step * 2; hs2 >= atm - step * 8; hs2 -= step) {
      var hd2 = ivMap[hs2];
      if (hd2 && hd2.peIv > 0 && hd2.peLtp >= 2) {
        if (!bestPeHedge || hd2.peIv < bestPeHedge.iv) bestPeHedge = { strike: hs2, iv: hd2.peIv, ltp: hd2.peLtp };
      }
    }
    if (bestCeHedge || bestPeHedge) {
      var hedgeDetail = '';
      if (bestCeHedge) hedgeDetail += 'CE: ' + bestCeHedge.strike + ' (IV ' + bestCeHedge.iv.toFixed(1) + '%, \u20b9' + bestCeHedge.ltp.toFixed(0) + ')';
      if (bestCeHedge && bestPeHedge) hedgeDetail += ' | ';
      if (bestPeHedge) hedgeDetail += 'PE: ' + bestPeHedge.strike + ' (IV ' + bestPeHedge.iv.toFixed(1) + '%, \u20b9' + bestPeHedge.ltp.toFixed(0) + ')';
      recs.push({ type: 'hedge', label: 'Best Hedges (Cheapest IV)',
        detail: hedgeDetail,
        reason: 'Lowest IV in OTM wing = cheapest protection per rupee.',
        color: '#06b6d4' });
    }

    // 4. BULL SPREAD — when direction is bullish
    if (dirScore >= 1) {
      var bullBuy = atm;
      var bullSell = atm + step * 3;
      var buyD = ivMap[bullBuy] || {};
      var sellD = ivMap[bullSell] || {};
      if (buyD.ceLtp > 0 && sellD.ceLtp > 0) {
        var cost = buyD.ceLtp - sellD.ceLtp;
        recs.push({ type: 'bull', label: 'Bull Call Spread',
          detail: 'Buy ' + bullBuy + 'CE (\u20b9' + buyD.ceLtp.toFixed(0) + ') / Sell ' + bullSell + 'CE (\u20b9' + sellD.ceLtp.toFixed(0) + ')',
          reason: 'Net debit \u20b9' + cost.toFixed(0) + '. Max profit \u20b9' + ((bullSell - bullBuy - cost) * 65).toFixed(0) + '/lot at ' + bullSell + '+.',
          color: '#00d4aa' });
      }
    }

    // 5. BEAR SPREAD — when direction is bearish
    if (dirScore <= -1) {
      var bearBuy = atm;
      var bearSell = atm - step * 3;
      var bearBuyD = ivMap[bearBuy] || {};
      var bearSellD = ivMap[bearSell] || {};
      if (bearBuyD.peLtp > 0 && bearSellD.peLtp > 0) {
        var bearCost = bearBuyD.peLtp - bearSellD.peLtp;
        recs.push({ type: 'bear', label: 'Bear Put Spread',
          detail: 'Buy ' + bearBuy + 'PE (\u20b9' + bearBuyD.peLtp.toFixed(0) + ') / Sell ' + bearSell + 'PE (\u20b9' + bearSellD.peLtp.toFixed(0) + ')',
          reason: 'Net debit \u20b9' + bearCost.toFixed(0) + '. Max profit \u20b9' + ((bearBuy - bearSell - bearCost) * 65).toFixed(0) + '/lot at ' + bearSell + '-.',
          color: '#ff4466' });
      }
    }

    return {
      atmIv: atmIv, atmCeIv: atmCeIv, atmPeIv: atmPeIv,
      peWingIv: avgPeWing, ceWingIv: avgCeWing,
      skewRatio: skewRatio, skewGrade: skewGrade,
      termStructure: termStructure,
      nearIv: atmIv, farIv: farIv,
      roughness: roughness,
      dirLabel: dirLabel, dirScore: dirScore, dirColor: dirColor,
      signals: signals, recs: recs,
      modeLabel: surfaceMode === 'ce' ? 'CE' : surfaceMode === 'pe' ? 'PE' : 'Avg'
    };
  }, [surfaceData, chains, expiries, atm, spot, surfaceMode, strikeRange]);

  // Smile data
  var smileData = useMemo(function () {
    if (!Object.keys(chains).length || !atm) return [];
    var selectedExpiries = smileOverlay ? expiries.filter(function (e) { return chains[e]; }) : (smileExpiry && chains[smileExpiry] ? [smileExpiry] : []);
    if (!selectedExpiries.length) return [];

    var step = 50;
    var minS = atm - strikeRange * step;
    var maxS = atm + strikeRange * step;
    var points = [];

    for (var s = minS; s <= maxS; s += step) {
      var pt = { strike: s, dist: s - atm };
      selectedExpiries.forEach(function (exp, idx) {
        var chain = chains[exp].chain || [];
        var item = chain.find(function (c) { return getStrike(c) === s; });
        var suffix = smileOverlay ? '_' + idx : '';
        var expSpot = chains[exp].spot || spot;
        var ceIvVal = getIV(item, 'CE', expSpot, exp);
        var peIvVal = getIV(item, 'PE', expSpot, exp);
        pt['ce' + suffix] = ceIvVal ? Math.round(ceIvVal * 100) / 100 : null;
        pt['pe' + suffix] = peIvVal ? Math.round(peIvVal * 100) / 100 : null;
        pt['ceOi' + suffix] = getOi(item, 'CE');
        pt['peOi' + suffix] = getOi(item, 'PE');
        pt['ceLtp' + suffix] = getLtp(item, 'CE');
        pt['peLtp' + suffix] = getLtp(item, 'PE');
      });
      points.push(pt);
    }
    return points;
  }, [chains, atm, strikeRange, smileExpiry, smileOverlay, expiries]);

  // Smile interpretation
  var smileInterp = useMemo(function () {
    if (!smileData.length) return null;
    var suffix = smileOverlay ? '_0' : '';

    var atmPt = smileData.find(function (p) { return p.strike === atm; }) || {};
    var atmCe = atmPt['ce' + suffix] || 0;
    var atmPe = atmPt['pe' + suffix] || 0;

    var maxCeIv = 0, maxCeStrike = 0, maxPeIv = 0, maxPeStrike = 0;
    var ceSlope = 0, peSlope = 0, ceCount = 0, peCount = 0;
    var prevCe = null, prevPe = null;
    smileData.forEach(function (p) {
      var ce = p['ce' + suffix]; var pe = p['pe' + suffix];
      if (ce && p.strike > atm) {
        if (ce > maxCeIv) { maxCeIv = ce; maxCeStrike = p.strike; }
        if (prevCe != null) { ceSlope += (ce - prevCe); ceCount++; }
        prevCe = ce;
      }
      if (pe && p.strike < atm) {
        if (pe > maxPeIv) { maxPeIv = pe; maxPeStrike = p.strike; }
        if (prevPe != null) { peSlope += (prevPe - pe); peCount++; }
        prevPe = pe;
      }
    });
    var avgCeSlope = ceCount ? ceSlope / ceCount : 0;
    var avgPeSlope = peCount ? peSlope / peCount : 0;

    var shape = 'Symmetric';
    if (avgPeSlope > avgCeSlope * 1.5 && avgPeSlope > 0.5) shape = 'Left skew (PE steep)';
    else if (avgCeSlope > avgPeSlope * 1.5 && avgCeSlope > 0.5) shape = 'Right skew (CE steep)';
    else if (avgPeSlope > 0.3 && avgCeSlope > 0.3) shape = 'Smile (wings up)';
    else if (avgPeSlope < 0.1 && avgCeSlope < 0.1) shape = 'Flat';

    var signals = [];
    if (shape === 'Left skew (PE steep)') signals.push({ type: 'warning', msg: 'PE wing steep — downside protection demand. Bearish undertone.' });
    else if (shape === 'Right skew (CE steep)') signals.push({ type: 'info', msg: 'CE wing rising — upside positioning or short covering.' });
    else if (shape === 'Smile (wings up)') signals.push({ type: 'info', msg: 'Both wings elevated — expects big move, direction uncertain.' });
    else if (shape === 'Flat') signals.push({ type: 'good', msg: 'Flat smile — low movement expected. Theta sellers paradise.' });
    else signals.push({ type: 'good', msg: 'Balanced smile — fair value across strikes.' });

    if (atmCe > 0 && atmPe > 0) {
      var diff = ((atmPe - atmCe) / Math.max(atmCe, atmPe) * 100);
      if (diff > 15) signals.push({ type: 'warning', msg: 'ATM: PE IV ' + diff.toFixed(0) + '% > CE — downside fear at money.' });
      else if (diff < -15) signals.push({ type: 'info', msg: 'ATM: CE IV ' + Math.abs(diff).toFixed(0) + '% > PE — upside demand at money.' });
    }
    if (maxPeIv > 50) signals.push({ type: 'danger', msg: 'PE wing ' + maxPeIv.toFixed(0) + '% at ' + maxPeStrike + ' — extreme put premium.' });
    if (maxCeIv > 50) signals.push({ type: 'warning', msg: 'CE wing ' + maxCeIv.toFixed(0) + '% at ' + maxCeStrike + ' — extreme call premium.' });

    return { shape: shape, atmCe: atmCe, atmPe: atmPe, maxCeIv: maxCeIv, maxPeIv: maxPeIv, signals: signals };
  }, [smileData, atm, smileOverlay]);

  // Render Plotly surface
  useEffect(function () {
    if (!surfaceData || !plotRef.current || !window.Plotly) return;
    var z = surfaceMode === 'ce' ? surfaceData.z_ce : surfaceMode === 'pe' ? surfaceData.z_pe : surfaceData.z_both;
    var label = surfaceMode === 'ce' ? 'CE IV' : surfaceMode === 'pe' ? 'PE IV' : 'Avg IV';

    var trace = {
      type: 'surface',
      x: surfaceData.strikes,
      y: surfaceData.expiries,
      z: z,
      colorscale: [
        [0, '#0c1b3a'], [0.15, '#0e4d92'], [0.3, '#1a9dff'],
        [0.45, '#00d4aa'], [0.6, '#fbbf24'],
        [0.75, '#f97316'], [0.9, '#ef4444'], [1, '#991b1b']
      ],
      colorbar: {
        title: { text: label + ' %', font: { color: '#8899aa', size: 11 } },
        tickfont: { color: '#8899aa', size: 10 },
        bgcolor: 'rgba(0,0,0,0)',
        borderwidth: 0,
        len: 0.6,
        thickness: 14
      },
      contours: {
        z: { show: true, usecolormap: true, highlightcolor: '#ffffff20', project: { z: false } }
      },
      hovertemplate: 'Strike: %{x}<br>Expiry: %{y}<br>IV: %{z:.1f}%<extra></extra>',
      lighting: { ambient: 0.7, diffuse: 0.5, specular: 0.2, roughness: 0.5 },
    };

    var layout = {
      paper_bgcolor: 'rgba(0,0,0,0)',
      plot_bgcolor: 'rgba(0,0,0,0)',
      font: { family: "'JetBrains Mono', monospace", color: '#8899aa', size: 10 },
      margin: { l: 0, r: 0, t: 30, b: 0 },
      scene: {
        camera: defaultCamera.current,
        xaxis: {
          title: { text: 'Strike', font: { size: 11, color: '#556677' } },
          gridcolor: '#1a2d4a30', color: '#556677',
          tickfont: { size: 9 }
        },
        yaxis: {
          title: { text: 'Expiry', font: { size: 11, color: '#556677' } },
          gridcolor: '#1a2d4a30', color: '#556677',
          tickfont: { size: 9 }
        },
        zaxis: {
          title: { text: 'IV %', font: { size: 11, color: '#556677' } },
          gridcolor: '#1a2d4a30', color: '#556677',
          tickfont: { size: 9 }
        },
        bgcolor: 'rgba(0,0,0,0)',
      },
      title: {
        text: 'NIFTY Implied Volatility Surface — ' + label,
        font: { size: 13, color: '#e2e8f0' },
        x: 0.02, xanchor: 'left'
      }
    };

    var config = {
      responsive: true,
      displayModeBar: false,
      scrollZoom: true
    };

    if (!plotInited.current) {
      window.Plotly.newPlot(plotRef.current, [trace], layout, config);
      plotInited.current = true;
    } else {
      window.Plotly.react(plotRef.current, [trace], layout);
    }
  }, [surfaceData, surfaceMode, plotlyReady]);

  var resetView = function () {
    if (plotRef.current) {
      window.Plotly.relayout(plotRef.current, { 'scene.camera': defaultCamera.current });
    }
  };

  var smileTooltip = function (props) {
    if (!props.active || !props.payload || !props.payload.length) return null;
    var d = props.payload[0].payload;
    return React.createElement('div', {
      style: {
        background: '#0d1526', border: '1px solid rgba(26,45,74,0.6)', borderRadius: 8,
        padding: '10px 14px', fontSize: 11, color: '#e2e8f0', minWidth: 180
      }
    },
      React.createElement('div', { style: { fontWeight: 700, marginBottom: 6, fontSize: 12 } },
        'Strike: ' + d.strike + ' (' + (d.dist > 0 ? '+' : '') + d.dist + ')'),
      props.payload.map(function (p, i) {
        return React.createElement('div', { key: i, style: { display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 2 } },
          React.createElement('span', { style: { color: p.color } }, p.name),
          React.createElement('span', { style: { fontWeight: 600 } }, p.value ? p.value.toFixed(1) + '%' : '--'));
      }),
      d.ceLtp != null && React.createElement('div', { style: { borderTop: '1px solid rgba(26,45,74,0.4)', marginTop: 6, paddingTop: 6, fontSize: 10, color: '#556677' } },
        'CE: \u20b9' + (d.ceLtp || d.ceLtp_0 || 0).toFixed(1) + ' OI:' + ((d.ceOi || d.ceOi_0 || 0) / 1000).toFixed(0) + 'K',
        React.createElement('br'),
        'PE: \u20b9' + (d.peLtp || d.peLtp_0 || 0).toFixed(1) + ' OI:' + ((d.peOi || d.peOi_0 || 0) / 1000).toFixed(0) + 'K')
    );
  };

  var sigColor = { danger: '#ff4466', warning: '#fbbf24', good: '#00d4aa', info: '#0ea5e9' };
  var sigBg = { danger: 'rgba(255,68,102,0.08)', warning: 'rgba(251,191,36,0.08)', good: 'rgba(0,212,170,0.08)', info: 'rgba(14,165,233,0.08)' };

  var smileColors = ['#0ea5e9', '#ff4466', '#00d4aa', '#f97316', '#a78bfa'];
  var selectedSmileExpiries = smileOverlay ? expiries.filter(function (e) { return chains[e]; }) : (smileExpiry ? [smileExpiry] : []);

  if (loading && !Object.keys(chains).length) {
    return React.createElement('div', {
      style: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', background: '#060b18', color: '#556677', fontFamily: "'JetBrains Mono', monospace" }
    }, 'Loading IV Surface...');
  }

  return React.createElement('div', {
    style: { display: 'flex', flexDirection: 'column', height: '100%', background: '#060b18', color: '#e2e8f0', fontFamily: "'JetBrains Mono', 'Fira Code', monospace", overflow: 'hidden' }
  },
    // Main content
    React.createElement('div', { style: { flex: 1, display: 'flex', overflow: 'hidden' } },

      // LEFT: 3D Surface
      React.createElement('div', { style: { flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 } },

        // Controls bar
        React.createElement('div', {
          style: { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', background: '#0d1526', borderBottom: '1px solid rgba(26,45,74,0.4)', flexWrap: 'wrap', flexShrink: 0 }
        },
          React.createElement('span', { style: { fontSize: 14, fontWeight: 800, color: '#e2e8f0', marginRight: 8 } }, 'IV Surface'),
          // Mode buttons
          ['both', 'ce', 'pe'].map(function (m) {
            return React.createElement('button', {
              key: m, onClick: function () { setSurfaceMode(m); },
              style: {
                padding: '4px 12px', borderRadius: 4, fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                background: surfaceMode === m ? 'rgba(14,165,233,0.15)' : 'transparent',
                color: surfaceMode === m ? '#0ea5e9' : '#556677',
                border: '1px solid ' + (surfaceMode === m ? 'rgba(14,165,233,0.4)' : 'rgba(26,45,74,0.4)')
              }
            }, m.toUpperCase());
          }),
          React.createElement('div', { style: { width: 1, height: 16, background: 'rgba(26,45,74,0.4)', margin: '0 4px' } }),
          // Strike range
          React.createElement('span', { style: { fontSize: 9, color: '#556677' } }, 'Strikes:'),
          [10, 20, 30].map(function (r) {
            return React.createElement('button', {
              key: r, onClick: function () { setStrikeRange(r); },
              style: {
                padding: '3px 8px', borderRadius: 4, fontSize: 9, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                background: strikeRange === r ? 'rgba(0,212,170,0.12)' : 'transparent',
                color: strikeRange === r ? '#00d4aa' : '#556677',
                border: '1px solid ' + (strikeRange === r ? 'rgba(0,212,170,0.3)' : 'rgba(26,45,74,0.3)')
              }
            }, '\u00b1' + r);
          }),
          React.createElement('div', { style: { width: 1, height: 16, background: 'rgba(26,45,74,0.4)', margin: '0 4px' } }),
          React.createElement('button', {
            onClick: resetView,
            style: { padding: '3px 10px', borderRadius: 4, fontSize: 9, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', background: 'transparent', color: '#a78bfa', border: '1px solid rgba(167,139,250,0.3)' }
          }, 'Reset View'),
          // Update interval
          React.createElement('div', { style: { marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 } },
            React.createElement('span', { style: { fontSize: 9, color: '#556677' } }, 'Update:'),
            [3, 5, 10].map(function (s) {
              return React.createElement('button', {
                key: s, onClick: function () { setUpdateInterval(s); },
                style: {
                  padding: '2px 6px', borderRadius: 3, fontSize: 9, cursor: 'pointer', fontFamily: 'inherit',
                  background: updateInterval === s ? 'rgba(14,165,233,0.12)' : 'transparent',
                  color: updateInterval === s ? '#0ea5e9' : '#3d5068',
                  border: '1px solid ' + (updateInterval === s ? 'rgba(14,165,233,0.3)' : 'rgba(26,45,74,0.2)')
                }
              }, s + 's');
            }),
            React.createElement('span', { style: { fontSize: 9, color: '#3d5068', marginLeft: 6 } }, lastUpdate)
          )
        ),

        // 3D Plot
        React.createElement('div', {
          ref: plotRef,
          style: { flex: 1, minHeight: 350 }
        })
      ),

      // RIGHT: Interpretation panel
      React.createElement('div', {
        style: { width: 260, flexShrink: 0, borderLeft: '1px solid rgba(26,45,74,0.3)', background: '#0a1020', padding: 14, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12 }
      },
        React.createElement('div', { style: { fontSize: 11, fontWeight: 800, color: '#556677', textTransform: 'uppercase', letterSpacing: 1 } }, 'Interpretation'),

        // Metrics
        interp && React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 } },
          [
            ['ATM IV', interp.atmIv.toFixed(1) + '%', '#e2e8f0'],
            ['Spot', spot ? spot.toLocaleString() : '--', '#e2e8f0'],
            ['CE ATM', interp.atmCeIv.toFixed(1) + '%', '#0ea5e9'],
            ['PE ATM', interp.atmPeIv.toFixed(1) + '%', '#ff4466'],
            ['CE Wing', interp.ceWingIv.toFixed(1) + '%', '#0ea5e9'],
            ['PE Wing', interp.peWingIv.toFixed(1) + '%', '#ff4466'],
            ['Skew', interp.skewRatio.toFixed(2) + 'x', interp.skewRatio > 1.5 ? '#fbbf24' : interp.skewRatio < 0.85 ? '#0ea5e9' : '#00d4aa'],
            ['Grade', interp.skewGrade, interp.skewGrade.includes('Steep') ? '#ff4466' : interp.skewGrade.includes('Heavy') ? '#fbbf24' : '#00d4aa'],
          ].map(function (m) {
            return React.createElement('div', {
              key: m[0],
              style: { background: 'rgba(13,21,38,0.8)', borderRadius: 6, padding: '6px 8px', border: '1px solid rgba(26,45,74,0.25)' }
            },
              React.createElement('div', { style: { fontSize: 8, color: '#556677', fontWeight: 600, textTransform: 'uppercase' } }, m[0]),
              React.createElement('div', { style: { fontSize: 15, fontWeight: 700, color: m[2], marginTop: 2 } }, m[1]));
          })
        ),

        // Term structure
        interp && React.createElement('div', {
          style: { background: 'rgba(13,21,38,0.8)', borderRadius: 6, padding: '8px 10px', border: '1px solid rgba(26,45,74,0.25)' }
        },
          React.createElement('div', { style: { fontSize: 8, color: '#556677', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 } }, 'Term Structure'),
          React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' } },
            React.createElement('span', { style: { fontSize: 12, fontWeight: 700, color: interp.termStructure === 'contango' ? '#00d4aa' : interp.termStructure === 'backwardation' ? '#ff4466' : '#8899aa' } },
              interp.termStructure === 'contango' ? '\u2197 Contango' : interp.termStructure === 'backwardation' ? '\u2198 Backwardation' : '\u2192 Flat'),
            React.createElement('span', { style: { fontSize: 9, color: '#556677' } }, interp.nearIv.toFixed(1) + ' \u2192 ' + interp.farIv.toFixed(1))),
          React.createElement('div', { style: { fontSize: 9, color: '#3d5068', marginTop: 4 } },
            'Roughness: ' + interp.roughness.toFixed(1))
        ),

        // Direction badge
        interp && React.createElement('div', {
          style: { background: 'rgba(13,21,38,0.8)', borderRadius: 6, padding: '8px 10px', border: '1px solid rgba(26,45,74,0.25)', textAlign: 'center' }
        },
          React.createElement('div', { style: { fontSize: 8, color: '#556677', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 } }, 'Probable Direction'),
          React.createElement('div', { style: { fontSize: 18, fontWeight: 900, color: interp.dirColor } }, interp.dirLabel),
          React.createElement('div', { style: { fontSize: 9, color: '#3d5068', marginTop: 2 } }, 'Score: ' + (interp.dirScore > 0 ? '+' : '') + interp.dirScore)
        ),

        // Signals
        React.createElement('div', { style: { fontSize: 9, fontWeight: 700, color: '#556677', textTransform: 'uppercase', letterSpacing: 0.5 } }, 'Surface Signals'),
        interp && interp.signals.map(function (s, i) {
          return React.createElement('div', {
            key: i,
            style: { padding: '8px 10px', borderRadius: 6, background: sigBg[s.type], borderLeft: '3px solid ' + sigColor[s.type], fontSize: 11, color: sigColor[s.type], lineHeight: 1.5 }
          }, s.msg);
        }),

        // Recommendations
        interp && interp.recs && interp.recs.length > 0 && React.createElement('div', { style: { fontSize: 9, fontWeight: 700, color: '#556677', textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 4 } }, 'Recommendations'),
        interp && interp.recs && interp.recs.map(function (r, i) {
          return React.createElement('div', {
            key: 'rec' + i,
            style: { padding: '8px 10px', borderRadius: 6, background: 'rgba(13,21,38,0.8)', border: '1px solid ' + r.color + '40', marginBottom: 0 }
          },
            React.createElement('div', { style: { fontSize: 10, fontWeight: 700, color: r.color, marginBottom: 3, textTransform: 'uppercase', letterSpacing: 0.5 } }, r.label),
            React.createElement('div', { style: { fontSize: 12, fontWeight: 600, color: '#e2e8f0', marginBottom: 4, fontFamily: "'JetBrains Mono', monospace" } }, r.detail),
            React.createElement('div', { style: { fontSize: 10, color: '#556677', lineHeight: 1.4 } }, r.reason)
          );
        }),

        // Legend
        React.createElement('div', {
          style: { marginTop: 'auto', padding: '8px 0', borderTop: '1px solid rgba(26,45,74,0.2)', fontSize: 9, color: '#3d5068' }
        },
          'Drag to rotate \u00b7 Scroll to zoom',
          React.createElement('br'),
          expiries.length + ' expiries \u00b7 ' + (strikeRange * 2) + ' strikes')
      )
    ),

    // BOTTOM: IV Smile
    React.createElement('div', {
      style: { height: 290, flexShrink: 0, borderTop: '1px solid rgba(26,45,74,0.4)', background: '#0a1020', padding: '8px 14px 4px', overflowY: 'auto' }
    },
      // Smile controls
      React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 } },
        React.createElement('span', { style: { fontSize: 11, fontWeight: 800, color: '#556677' } }, 'IV SMILE'),
        React.createElement('div', { style: { width: 1, height: 14, background: 'rgba(26,45,74,0.3)' } }),
        !smileOverlay && React.createElement('select', {
          value: smileExpiry,
          onChange: function (e) { setSmileExpiry(e.target.value); },
          style: { background: '#0d1526', color: '#e2e8f0', border: '1px solid rgba(26,45,74,0.4)', borderRadius: 4, padding: '2px 6px', fontSize: 10, fontFamily: 'inherit' }
        }, expiries.map(function (e) {
          var d = new Date(e);
          return React.createElement('option', { key: e, value: e }, d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }));
        })),
        React.createElement('button', {
          onClick: function () { setSmileOverlay(!smileOverlay); },
          style: {
            padding: '3px 10px', borderRadius: 4, fontSize: 9, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
            background: smileOverlay ? 'rgba(167,139,250,0.12)' : 'transparent',
            color: smileOverlay ? '#a78bfa' : '#556677',
            border: '1px solid ' + (smileOverlay ? 'rgba(167,139,250,0.3)' : 'rgba(26,45,74,0.3)')
          }
        }, smileOverlay ? 'All Expiries' : 'Overlay All'),
        React.createElement('span', { style: { marginLeft: 'auto', fontSize: 9, color: '#3d5068' } }, 'ATM: ' + atm)
      ),

      // Smile chart
      React.createElement(ResponsiveContainer, { width: '100%', height: 190 },
        React.createElement(LineChart, { data: smileData, margin: { top: 5, right: 20, bottom: 5, left: 10 } },
          React.createElement(XAxis, {
            dataKey: 'strike', tick: { fontSize: 9, fill: '#556677' },
            tickFormatter: function (v) { return v === atm ? 'ATM' : v; },
            interval: Math.max(1, Math.floor(smileData.length / 15))
          }),
          React.createElement(YAxis, {
            tick: { fontSize: 9, fill: '#556677' },
            tickFormatter: function (v) { return v.toFixed(0) + '%'; },
            domain: ['auto', 'auto'], width: 40
          }),
          React.createElement(Tooltip, { content: smileTooltip }),
          React.createElement(ReferenceLine, { x: atm, stroke: '#fbbf2440', strokeDasharray: '3 3', label: { value: 'ATM', fill: '#fbbf24', fontSize: 9, position: 'top' } }),
          // CE lines
          selectedSmileExpiries.map(function (exp, idx) {
            var suffix = smileOverlay ? '_' + idx : '';
            var d = new Date(exp);
            var label = d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
            return React.createElement(Line, {
              key: 'ce' + idx, type: 'monotone', dataKey: 'ce' + suffix,
              stroke: smileOverlay ? smileColors[idx % smileColors.length] : '#0ea5e9',
              strokeWidth: 1.5, dot: false, connectNulls: true,
              name: 'CE' + (smileOverlay ? ' ' + label : '')
            });
          }),
          // PE lines
          selectedSmileExpiries.map(function (exp, idx) {
            var suffix = smileOverlay ? '_' + idx : '';
            var d = new Date(exp);
            var label = d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
            return React.createElement(Line, {
              key: 'pe' + idx, type: 'monotone', dataKey: 'pe' + suffix,
              stroke: smileOverlay ? smileColors[idx % smileColors.length] : '#ff4466',
              strokeWidth: 1.5, dot: false, connectNulls: true,
              strokeDasharray: smileOverlay ? undefined : '4 2',
              name: 'PE' + (smileOverlay ? ' ' + label : '')
            });
          }),
          React.createElement(Legend, {
            wrapperStyle: { fontSize: 10, color: '#556677' },
            iconSize: 10
          })
        )
      ),

      // Smile interpretation
      smileInterp && React.createElement('div', {
        style: { display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap', alignItems: 'flex-start' }
      },
        React.createElement('div', {
          style: { padding: '4px 10px', borderRadius: 4, background: 'rgba(13,21,38,0.8)', border: '1px solid rgba(26,45,74,0.3)', fontSize: 10, color: '#e2e8f0' }
        }, 'Shape: ', React.createElement('span', { style: { fontWeight: 700, color: '#a78bfa' } }, smileInterp.shape)),
        smileInterp.atmCe > 0 && React.createElement('div', {
          style: { padding: '4px 10px', borderRadius: 4, background: 'rgba(13,21,38,0.8)', border: '1px solid rgba(26,45,74,0.3)', fontSize: 10 }
        },
          React.createElement('span', { style: { color: '#0ea5e9' } }, 'CE: ' + smileInterp.atmCe.toFixed(1) + '%'),
          ' / ',
          React.createElement('span', { style: { color: '#ff4466' } }, 'PE: ' + smileInterp.atmPe.toFixed(1) + '%'),
          ' ATM'
        ),
        smileInterp.signals.map(function (s, i) {
          return React.createElement('div', {
            key: i,
            style: { padding: '4px 10px', borderRadius: 4, background: sigBg[s.type], borderLeft: '2px solid ' + sigColor[s.type], fontSize: 10, color: sigColor[s.type], maxWidth: 400 }
          }, s.msg);
        })
      )
    )
  );
}
